import { Select } from '@99designs/common/components/Select';
import { Field, FieldSet } from 'common/components/Form';
import TextArea from 'common/components/TextArea';
import AttachmentUploader, { Attachment } from 'common/components/uploader/AttachmentUploader';
import Section from 'launch/components/AppSection';
import BriefFooter from 'launch/components/BriefFooter';
import EmailField from 'launch/components/fields/EmailField';
import GenerativeAiPreferenceField from 'launch/components/fields/GenerativeAiPreferenceField';
import PrefillBriefField from 'launch/components/fields/PrefillBriefField';
import StockImagesField from 'launch/components/fields/StockImagesField';
import FieldSetInterjection from 'launch/components/FieldSetInterjection';
import getAiErrorMessage from 'launch/components/getAiErrorMessage';
import provideBriefStep, { StepProps } from 'launch/containers/provideBriefStep';
import React, { Component } from 'react';
import { FormattedMessage } from 'react-intl';

interface Fields {
    email?: string;
    prefill?: string;
    categoryKey: string;
    styleTheme?: string;
    existingLogo: Attachment[];
    requirement?: string;
    thingsToAvoid?: string;
    attachments: Attachment[];
    language: string;
    allowStockImages: string;
    allowGenerativeAi: string;
}

type Props = StepProps<Fields> &
    Fields & {
        location: {
            search: string;
        };
    };

class FacebookCoverDesignBrief extends Component<Props> {
    formatMessage(key: string, values = {}) {
        return <FormattedMessage id={`facebook_cover_design.${key}`} values={values} />;
    }

    handleChange<K extends keyof Fields>(field: K) {
        return (value: Fields[K]) => this.props.onChange(field, value);
    }

    render() {
        const t = this.formatMessage;

        return (
            <Section>
                <EmailField onChange={this.handleChange('email')} value={this.props.email} />

                <PrefillBriefField value={this.props.prefill} />

                {this.props.languageOptions.length > 0 && (
                    <FieldSet title={t('fieldset.language')}>
                        <Field
                            title={t('field.language.label')}
                            message={t('field.language.message')}
                            htmlFor="language"
                        >
                            <Select
                                id="language"
                                onChange={({ value }) => this.props.onChange('language', value)}
                                value={this.props.language}
                                options={this.props.languageOptions}
                            />
                        </Field>
                    </FieldSet>
                )}

                <FieldSet title={t('fieldset.visual_style')}>
                    <Field
                        title={t('field.style_theme.label')}
                        message={t('field.style_theme.message')}
                        htmlFor="style_theme"
                    >
                        <TextArea
                            id="style_theme"
                            value={this.props.styleTheme}
                            onChange={this.handleChange('styleTheme')}
                        />
                    </Field>
                </FieldSet>

                <FieldSet
                    title={t('fieldset.content_details')}
                    interjection={<FieldSetInterjection type="panda" />}
                >
                    <Field
                        title={t('field.existing_logo.label')}
                        message={t('field.existing_logo.message')}
                    >
                        <AttachmentUploader
                            id="existing_logo"
                            value={this.props.existingLogo}
                            onChange={this.handleChange('existingLogo')}
                        />
                    </Field>

                    <Field
                        title={t('field.requirement.label')}
                        message={t('field.requirement.message')}
                        htmlFor="requirement"
                    >
                        <TextArea
                            id="requirement"
                            value={this.props.requirement}
                            onChange={this.handleChange('requirement')}
                        />
                    </Field>

                    <Field
                        title={t('field.things_to_avoid.label')}
                        message={t('field.things_to_avoid.message')}
                        htmlFor="things_to_avoid"
                    >
                        <TextArea
                            id="things_to_avoid"
                            value={this.props.thingsToAvoid}
                            onChange={this.handleChange('thingsToAvoid')}
                        />
                    </Field>
                </FieldSet>

                <FieldSet title={t('fieldset.other')}>
                    {this.props.hasAiPreference && (
                        <GenerativeAiPreferenceField
                            onChange={this.handleChange('allowGenerativeAi')}
                            value={this.props.allowGenerativeAi}
                            errorMessage={getAiErrorMessage({
                                location: this.props.location,
                                hasAllowAiValue: !!this.props.allowGenerativeAi,
                            })}
                        />
                    )}
                    <StockImagesField
                        categoryKey={this.props.categoryKey}
                        onChange={this.handleChange('allowStockImages')}
                        value={this.props.allowStockImages}
                    />
                    <Field
                        title={t('field.attachments.label')}
                        message={t('field.attachments.message')}
                    >
                        <AttachmentUploader
                            id="attachments"
                            value={this.props.attachments}
                            onChange={this.handleChange('attachments')}
                        />
                    </Field>
                </FieldSet>

                <BriefFooter
                    onSave={this.props.onSave}
                    onContinue={this.props.onContinue}
                    isSaving={this.props.isSaving}
                />
            </Section>
        );
    }
}

export default provideBriefStep(FacebookCoverDesignBrief);
export { FacebookCoverDesignBrief as Component };
